/**
 * DESIGN SYSTEM NOTE: Hard-coded Tailwind colors in this file (bg-[#ECF1F3], text-[#43798F], etc.)
 * are intentional and match approved Figma designs. Platform CSS variables will be updated
 * to match Figma in a future initiative, then this file will be refactored to use semantic tokens.
 * See PR #9637 for context on the Figma-first strategy.
 */

import { observer } from 'mobx-react';
import { useContext, useState, useMemo } from 'react';
import {
  ChevronDownIcon,
  ChevronUpIcon,
  XMarkIcon,
  AcademicCapIcon,
} from '@heroicons/react/24/outline';
import { ClientExperienceResource } from '../../../Types';
import { emptyFragment } from '../../../../../../../../EmptyFragment';
import { trackIntercomEvent } from '../../../../../../../../services/intercomTrackingService';
import { T, TranslationsContext, translation } from '../../../../../../../../Translations';

interface Props {
  addedCourses: ClientExperienceResource[];
  onRemoveCourse: (courseId: number) => void;
  sectionName: string;
}

function CollectionTray({ addedCourses, onRemoveCourse, sectionName }: Props) {
  const ts = useContext(TranslationsContext);
  const [isExpanded, setIsExpanded] = useState(false);

  const handleToggleExpand = () => {
    const newExpandedState = !isExpanded;
    trackIntercomEvent('Builder V2 - Course Catalog - Toggle Collection Tray', {
      action: newExpandedState ? 'expand' : 'collapse',
      courseCount: addedCourses.length,
    });
    setIsExpanded(newExpandedState);
  };

  const handleRemoveCourse = (courseId: number, courseTitle: string) => {
    trackIntercomEvent('Builder V2 - Course Catalog - Remove Course from Tray', {
      courseId,
      courseTitle,
    });
    onRemoveCourse(courseId);
  };

  // Calculate aggregate stats
  const stats = useMemo(() => {
    const totalHours = addedCourses.reduce((sum, course) => {
      const hours = course.payload.total_hours.getOrElseValue(0);
      return sum + hours;
    }, 0);

    const uniqueCompetencies = new Set(
      addedCourses.flatMap((course) => course.payload.competency_names || []),
    );

    const uniqueSchools = new Set(
      addedCourses.map((course) => course.payload.school_partner_name).filter(Boolean),
    );

    return {
      totalCourses: addedCourses.length,
      totalHours,
      competencyCount: uniqueCompetencies.size,
      schoolCount: uniqueSchools.size,
      competencies: Array.from(uniqueCompetencies),
    };
  }, [addedCourses]);

  return (
    <div
      className="border-t border-border bg-card flex-shrink-0"
      data-intercom-target="builder-v2-collection-tray"
    >
      {addedCourses.length > 0 ? (
        <>
          {/* Tray Header */}
          <button
            onClick={handleToggleExpand}
            className="w-full px-6 py-3 flex items-center justify-between hover:bg-muted/30 transition-colors"
            data-intercom-target="builder-v2-collection-tray-toggle"
          >
            <div className="flex items-center gap-3">
              <h4 style={{ fontWeight: 'var(--font-weight-semibold)' }}>
                <T kind="Included in this section:" />
              </h4>
              <p className="text-muted-foreground" style={{ fontSize: 'var(--text-sm)' }}>
                {stats.totalCourses} course{stats.totalCourses !== 1 ? 's' : ''}
                {stats.totalHours > 0 && <> · {stats.totalHours}h</>}
                {stats.competencyCount > 0 && (
                  <>
                    {' '}
                    · {stats.competencyCount} competenc{stats.competencyCount !== 1 ? 'ies' : 'y'}
                  </>
                )}
                {stats.schoolCount > 0 && (
                  <>
                    {' '}
                    · {stats.schoolCount} school{stats.schoolCount !== 1 ? 's' : ''}
                  </>
                )}
              </p>
            </div>
            {isExpanded ? (
              <ChevronDownIcon className="w-5 h-5 text-muted-foreground" />
            ) : (
              <ChevronUpIcon className="w-5 h-5 text-muted-foreground" />
            )}
          </button>

          {/* Expanded Content */}
          {isExpanded && (
            <div className="px-6 pb-6 space-y-6 max-h-96 overflow-y-auto">
              {/* Course Chips */}
              <div className="flex flex-wrap gap-2">
                {addedCourses.map((course) => {
                  const hours = course.payload.total_hours.getOrElseValue(0);
                  return (
                    <div
                      key={course.payload.id}
                      className="flex items-center gap-2 px-3 py-2 bg-background border border-border rounded-md"
                    >
                      <span
                        className="truncate max-w-[200px]"
                        style={{ fontSize: 'var(--text-sm)' }}
                      >
                        {course.payload.title}
                      </span>
                      {hours > 0 && (
                        <span
                          className="text-muted-foreground"
                          style={{ fontSize: 'var(--text-xs)' }}
                        >
                          {hours}h
                        </span>
                      )}
                      <button
                        onClick={() => handleRemoveCourse(course.payload.id, course.payload.title)}
                        className="p-0.5 hover:bg-muted rounded transition-colors"
                        aria-label={translation('Remove course')(ts)}
                        data-intercom-target="builder-v2-remove-course-from-tray"
                      >
                        <XMarkIcon className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  );
                })}
              </div>

              {/* Competency Coverage */}
              {stats.competencies.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <AcademicCapIcon className="w-4 h-4 text-accent" />
                    <h4 style={{ fontWeight: 'var(--font-weight-medium)' }}>
                      <T kind="Competency Coverage" />
                    </h4>
                    <span className="text-muted-foreground" style={{ fontSize: 'var(--text-sm)' }}>
                      {stats.competencyCount} included
                    </span>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {stats.competencies.slice(0, 12).map((competency) => (
                      <span
                        key={competency}
                        className="px-3 py-1.5 rounded-md border bg-[#ECF1F3] text-[#43798F] border-border"
                        style={{ fontSize: 'var(--text-sm)' }}
                      >
                        {competency}
                      </span>
                    ))}
                    {stats.competencies.length > 12 && (
                      <span
                        className="px-3 py-1.5 rounded-full border bg-gray-100 text-gray-600 border-gray-300"
                        style={{ fontSize: 'var(--text-sm)' }}
                      >
                        +{stats.competencies.length - 12}
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      ) : (
        emptyFragment()
      )}
    </div>
  );
}

export default observer(CollectionTray);
